import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { ReviewListItemProps } from './review-list/review-list-item';

export type LoadedDiffDoc = {
  isNew: boolean;
  diffTitle: string;
  diffContent: React.ReactNode;
};

type ReviewsSelectionValue = {
  selectedItem: ReviewListItemProps | null;
  setSelectedItem: (item: ReviewListItemProps | null) => void;
  loadedDiffDoc: LoadedDiffDoc | null;
  setLoadedDiffDoc: (doc: LoadedDiffDoc | null) => void;
  loadingDiff: boolean;
  setLoadingDiff: (loading: boolean) => void;
  onApproveAllSuggestions: () => void;
  onRejectAllSuggestions: () => void;
  onAcceptAndPublish?: () => void;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  breadcrumb?: string;
  suggestionReasonBody?: string;
};

const ReviewsContext = createContext<ReviewsSelectionValue | null>(null);

export type ReviewsProviderProps = {
  children: React.ReactNode;
  /** Optional initial selection (e.g. from server or mock data) */
  defaultSelectedItem?: ReviewListItemProps | null;
  onApproveAllSuggestions: () => void;
  onRejectAllSuggestions: () => void;
  onAcceptAndPublish?: () => void;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  breadcrumb?: string;
  suggestionReasonBody?: string;
};

export function ReviewsSelectionProvider({
  children,
  defaultSelectedItem = null,
  onApproveAllSuggestions,
  onRejectAllSuggestions,
  onAcceptAndPublish,
  onOpenArticleLink,
  onSourceLinkClick,
  breadcrumb,
  suggestionReasonBody,
}: ReviewsProviderProps) {
  const [userSelectedItem, setUserSelectedItem] = useState<ReviewListItemProps | null>(null);
  const selectedItem = userSelectedItem ?? defaultSelectedItem;

  const [loadedDiffDoc, setLoadedDiffDoc] = useState<LoadedDiffDoc | null>(null);

  const [loadingDiff, setLoadingDiff] = useState(false);

  const handleSelect = useCallback((item: ReviewListItemProps | null) => setUserSelectedItem(item), []);
  const value = useMemo<ReviewsSelectionValue>(
    () => ({
      selectedItem,
      setSelectedItem: handleSelect,
      loadedDiffDoc,
      setLoadedDiffDoc,
      loadingDiff,
      setLoadingDiff,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onAcceptAndPublish,
      onOpenArticleLink,
      onSourceLinkClick,
      breadcrumb,
      suggestionReasonBody,
    }),
    [
      selectedItem,
      handleSelect,
      loadedDiffDoc,
      loadingDiff,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onAcceptAndPublish,
      onOpenArticleLink,
      onSourceLinkClick,
      breadcrumb,
      suggestionReasonBody,
    ],
  );

  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
}

export function useReviewsSelection(): ReviewsSelectionValue {
  const ctx = useContext(ReviewsContext);
  if (!ctx) {
    throw new Error('useReviewsSelection must be used within ReviewsSelectionProvider');
  }

  return ctx;
}
